import { FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import ReactSelect from '@components/atoms/ReactSelect';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { SubscriptionPlanTemplate, PlanAddonSummary } from '@models/subscription';
import { Button } from '@components/atoms/Button';
import { Checkbox } from '@components/atoms/Checkbox';
import { getTranslatedRoleLabel } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';
import {
  SEAT_COUNTING_ROLE_OPTIONS,
  DEFAULT_OFFICE_SEAT_COUNTING_ROLES,
} from '@constants/seatCountingRoles';
import { useSubscriptionPlans } from '@hooks/api/subscription-plans';
import {
  useLinkAddonToPlan,
  useUnlinkAddonFromPlan,
  useRepriceAddonLink,
} from '@hooks/api/subscription-plans/useLinkAddonMutations';

interface LinkedAddonRow {
  addonId: string;
  addonName: string;
  linked: boolean;
  priceCents: number;
}

interface CreateEditPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Sprint 17 develop run 2 (review finding, story-082 Warning #1): resolves with the created
  // plan (id included) so a new plan's chosen add-ons can be linked in the same submit, instead
  // of requiring a save-then-reopen round trip.
  onCreate?: (data: any) => Promise<SubscriptionPlanTemplate | void> | SubscriptionPlanTemplate | void;
  onUpdate?: (planId: string, data: any) => void;
  /** Resolves true when the plan was deleted, false when it failed (never rejects). */
  onDelete?: (planId: string) => Promise<boolean>;
  saving: boolean;
  deleting?: boolean;
  editing?: boolean;
  initialData?: SubscriptionPlanTemplate;
}

// Yup validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  basePriceCents: Yup.number()
    .required('Base price is required')
    .min(1, 'Price must be at least $1'),
  includedSeats: Yup.number()
    .required('Included seats is required')
    .min(1, 'Must be at least 1')
    .integer('Must be a whole number'),
  isPublic: Yup.boolean().required(),
  isAddOn: Yup.boolean().required(),
  requiresPaymentUpfront: Yup.boolean().required(),
  // A plan needs at least one billing gate. Without an upfront card, the trial is what
  // eventually forces one — so a card-free plan must define a trial. The backend rejects
  // this combination too; this only surfaces it before the round-trip.
  trialDurationDays: Yup.number()
    .nullable()
    .min(0, 'Must be 0 or more')
    .integer('Must be a whole number')
    .when('requiresPaymentUpfront', {
      is: false,
      then: (schema) =>
        schema
          .required('A card-free plan must define a trial period')
          .min(1, 'A card-free plan must define a trial period of at least 1 day'),
    }),
  validFrom: Yup.string().nullable(),
  validTo: Yup.string()
    .nullable()
    .test('is-after-validFrom', 'Valid To must be after Valid From', function (value) {
      const { validFrom } = this.parent;
      if (!value || !validFrom) return true;
      return new Date(value) > new Date(validFrom);
    }),
});

const CreateEditPlanModal: FC<CreateEditPlanModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  saving,
  deleting = false,
  editing = false,
  initialData,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // Sprint 31, develop run 2 (priority 3, role-name sweep). This SUPER_ADMIN-only screen is
  // otherwise untranslated (out of this pass's scope, matching sprint 30's fieldCaptureAdmin
  // precedent) — these two hooks exist only because `SEAT_COUNTING_ROLE_OPTIONS` dropped its own
  // `label` field in favor of the shared `enumLabels.role*` map, which this checkbox list must
  // now read from directly.
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');

  // Sprint 17, story-082: "Linked Add-ons" section state, independent of formik — this section
  // has its own diff-and-save flow (three mutations) rather than folding into the plan
  // create/update payload.
  const { data: allPlans = [] } = useSubscriptionPlans();
  const addonTemplates = useMemo(() => allPlans.filter((p) => p.isAddOn), [allPlans]);
  const [linkedAddonRows, setLinkedAddonRows] = useState<LinkedAddonRow[]>([]);
  const linkAddonToPlan = useLinkAddonToPlan();
  const unlinkAddonFromPlan = useUnlinkAddonFromPlan();
  const repriceAddonLink = useRepriceAddonLink();

  useEffect(() => {
    if (!isOpen) return;
    const existingLinks = initialData?.addOns ?? [];
    setLinkedAddonRows(
      addonTemplates.map((addon) => {
        const existing = existingLinks.find((l) => l.id === addon.id);
        return {
          addonId: addon.id,
          addonName: addon.name,
          linked: !!existing,
          priceCents: existing?.priceCents ?? addon.basePriceCents,
        };
      }),
    );
    // Only re-derive when the modal (re)opens for a given plan — not on every addonTemplates
    // refetch, which would stomp in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData?.id]);

  const toggleAddonLinked = (addonId: string) => {
    setLinkedAddonRows((rows) =>
      rows.map((row) => (row.addonId === addonId ? { ...row, linked: !row.linked } : row)),
    );
  };

  const setAddonPrice = (addonId: string, priceDollars: number) => {
    setLinkedAddonRows((rows) =>
      rows.map((row) =>
        row.addonId === addonId ? { ...row, priceCents: Math.round(priceDollars * 100) } : row,
      ),
    );
  };

  /**
   * Fires link/unlink/reprice only for rows whose checked state or price changed since
   * `existingLinks`. Takes `planId`/`existingLinks` as arguments (rather than reading
   * `initialData` directly) so the same helper serves both the edit path (existing plan, existing
   * links) and the create path (freshly-created plan id, no prior links — see the Create-mode
   * submit branch below).
   *
   * Sprint 17 develop run 2 (review finding, story-082 Warning #1): each row's mutation is
   * caught individually so one failed link doesn't abort the rest — the underlying mutation hooks
   * already toast their own per-row error; this also collects which add-ons failed so the caller
   * can report a partial failure honestly (the plan itself was already saved) instead of a
   * misleading "the whole save failed."
   */
  const saveLinkedAddonChanges = async (
    planId: string,
    existingLinks: PlanAddonSummary[],
  ): Promise<string[]> => {
    const failedAddonNames: string[] = [];

    for (const row of linkedAddonRows) {
      const existing = existingLinks.find((l) => l.id === row.addonId);
      const wasLinked = !!existing;

      try {
        if (row.linked && !wasLinked) {
          await linkAddonToPlan.mutateAsync({ planId, addonId: row.addonId, priceCents: row.priceCents });
        } else if (!row.linked && wasLinked) {
          await unlinkAddonFromPlan.mutateAsync({ planId, addonId: row.addonId });
        } else if (row.linked && wasLinked && existing.priceCents !== row.priceCents) {
          await repriceAddonLink.mutateAsync({ planId, addonId: row.addonId, priceCents: row.priceCents });
        }
      } catch {
        // Already toasted by the failing mutation's own onError handler.
        failedAddonNames.push(row.addonName);
      }
    }

    return failedAddonNames;
  };

  const formik = useFormik({
    initialValues: {
      name: initialData?.name || '',
      basePriceCents: initialData ? initialData.basePriceCents / 100 : 1, // Convert cents to dollars
      includedSeats: initialData?.includedSeats || 1,
      isPublic: initialData?.isPublic ?? true,
      isAddOn: initialData?.isAddOn ?? false,
      seatCountingRoles:
        initialData?.seatCountingRoles ?? DEFAULT_OFFICE_SEAT_COUNTING_ROLES,
      trialDurationDays: initialData?.trialDurationDays || null,
      requiresPaymentUpfront: initialData?.requiresPaymentUpfront ?? true,
      validFrom: initialData?.validFrom
        ? new Date(initialData.validFrom).toISOString().split('T')[0]
        : '',
      validTo: initialData?.validTo
        ? new Date(initialData.validTo).toISOString().split('T')[0]
        : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        basePriceCents: Math.round(values.basePriceCents * 100), // Convert dollars to cents
        includedSeats: values.includedSeats,
        isPublic: values.isPublic,
        isAddOn: values.isAddOn,
        seatCountingRoles: values.seatCountingRoles,
        trialDurationDays: values.trialDurationDays || undefined,
        requiresPaymentUpfront: values.requiresPaymentUpfront,
        validFrom: values.validFrom || undefined,
        validTo: values.validTo || undefined,
      };

      if (editing && initialData) {
        onUpdate && onUpdate(initialData.id, payload);
        await saveLinkedAddonChanges(initialData.id, initialData.addOns ?? []);
      } else if (onCreate) {
        // Sprint 17 develop run 2 (review finding, story-082 Warning #1): resolve the created
        // plan's id, then link any add-ons the SUPER_ADMIN already checked before the plan
        // existed — the deferred half of the same diff-and-save helper the edit path uses. A
        // brand-new plan has no prior links, so `existingLinks` is always empty here.
        const created = await onCreate(payload);
        if (created?.id && !values.isAddOn) {
          const failedAddonNames = await saveLinkedAddonChanges(created.id, []);
          if (failedAddonNames.length) {
            toast.error(
              `Plan "${values.name}" was created, but failed to link: ${failedAddonNames.join(', ')}. Edit the plan to retry.`,
            );
          }
        }
      }
    },
  });

  const { values, errors, touched, handleChange, submitForm, resetForm, setFieldValue } = formik;

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setIsDeleteModalOpen(false);
    }
  }, [isOpen, resetForm]);

  const handleDelete = async () => {
    if (!initialData || !onDelete) return;
    // The page handler never throws and already toasted any failure; the confirmation stays
    // open on `false` so the user can cancel or retry.
    const deleted = await onDelete(initialData.id);
    if (deleted) {
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isLoading={saving}
        handleConfirm={submitForm}
        title={editing ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
        confirmText={editing ? 'Update' : 'Create'}
        size="large">
        <form className="flex flex-col gap-6">
          {/* Name */}
          <FormGroup
            error={touched.name && errors.name ? errors.name : undefined}
            htmlFor="name"
            label="Plan Name">
            <Input
              name="name"
              value={values.name}
              onChange={handleChange}
              placeholder="e.g., BASE, STANDARD, PRO"
              error={!!(touched.name && errors.name)}
            />
          </FormGroup>

          {/* Base Price and Included Seats - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <FormGroup
              error={
                touched.basePriceCents && errors.basePriceCents ? errors.basePriceCents : undefined
              }
              htmlFor="basePriceCents"
              label="Base Price (USD)">
              <Input
                name="basePriceCents"
                type="number"
                step="0.01"
                value={values.basePriceCents}
                onChange={handleChange}
                placeholder="99.00"
                error={!!(touched.basePriceCents && errors.basePriceCents)}
              />
            </FormGroup>

            <FormGroup
              error={
                touched.includedSeats && errors.includedSeats ? errors.includedSeats : undefined
              }
              htmlFor="includedSeats"
              label="Included Seats">
              <Input
                name="includedSeats"
                type="number"
                value={values.includedSeats}
                onChange={handleChange}
                placeholder="5"
                error={!!(touched.includedSeats && errors.includedSeats)}
              />
            </FormGroup>
          </div>

          {/* Billing gate: card upfront, or a trial that eventually forces one */}
          <FormGroup htmlFor="requiresPaymentUpfront" label="Payment">
            <Checkbox
              name="requiresPaymentUpfront"
              checked={values.requiresPaymentUpfront}
              onChange={(checked) =>
                setFieldValue('requiresPaymentUpfront', checked)
              }
              label="Require a credit card at sign-up"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {values.requiresPaymentUpfront
                ? 'The card is captured before the organization is created.'
                : 'No card is captured at sign-up. The organization runs on a trial and is asked to pay when the trial expires, so a trial period is required below.'}
            </p>
          </FormGroup>

          {/* Trial Duration Days */}
          <FormGroup
            error={
              touched.trialDurationDays && errors.trialDurationDays
                ? errors.trialDurationDays
                : undefined
            }
            htmlFor="trialDurationDays"
            label={
              values.requiresPaymentUpfront
                ? 'Trial Duration (Days)'
                : 'Trial Duration (Days) — required'
            }>
            <Input
              name="trialDurationDays"
              type="number"
              value={values.trialDurationDays || ''}
              onChange={handleChange}
              placeholder="30"
              error={!!(touched.trialDurationDays && errors.trialDurationDays)}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {values.requiresPaymentUpfront
                ? 'Leave empty or set to 0 for no trial period'
                : 'A card-free plan must define a trial — otherwise it has no billing gate at all.'}
            </p>
          </FormGroup>

          {/* Checkboxes - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <FormGroup htmlFor="isPublic" label="Visibility">
              <ReactSelect
                value={
                  values.isPublic ? { value: 'true', label: 'Public' } : { value: 'false', label: 'Private' }
                }
                onChange={(value: any) => setFieldValue('isPublic', value?.value === 'true')}
                options={[
                  { value: 'true', label: 'Public' },
                  { value: 'false', label: 'Private' },
                ]}
                isClearable={false}
              />
            </FormGroup>

            <FormGroup htmlFor="isAddOn" label="Type">
              <ReactSelect
                value={
                  values.isAddOn ? { value: 'true', label: 'Add-on' } : { value: 'false', label: 'Plan' }
                }
                onChange={(value: any) => setFieldValue('isAddOn', value?.value === 'true')}
                options={[
                  { value: 'false', label: 'Plan' },
                  { value: 'true', label: 'Add-on' },
                ]}
                isClearable={false}
              />
            </FormGroup>
          </div>

          {/* Seat-counting roles */}
          <FormGroup htmlFor="seatCountingRoles" label="Roles that count as seats">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Users with a checked role occupy a billable seat under this plan.
              Unchecked roles never count toward the seat limit.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {SEAT_COUNTING_ROLE_OPTIONS.map((role) => {
                const checked = values.seatCountingRoles.includes(role.id);
                return (
                  <Checkbox
                    key={role.id}
                    id={`seatCountingRoles-${role.id}`}
                    label={getTranslatedRoleLabel(role.id, tEnumLabels, tCommon.unknown)}
                    checked={checked}
                    onChange={(next) => {
                      const current: string[] = values.seatCountingRoles;
                      setFieldValue(
                        'seatCountingRoles',
                        next
                          ? [...current, role.id]
                          : current.filter((r) => r !== role.id),
                      );
                    }}
                  />
                );
              })}
            </div>
          </FormGroup>

          {/* Sprint 17, story-082: Linked Add-ons — only on a base plan (a plan editing itself
              as an add-on has no add-ons of its own). Available in both Create and Edit: on
              Save, link/unlink/reprice apply immediately when editing, and — since develop run
              2 (review finding, story-082 Warning #1) — right after creation when creating, so
              a brand-new plan no longer needs a save-then-reopen round trip to gain add-ons. */}
          {!values.isAddOn && (
            <FormGroup htmlFor="linkedAddons" label="Linked Add-ons">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Add-ons purchasable by orgs on this plan, and the price each costs on this plan
                specifically.
              </p>
              {addonTemplates.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No add-on plans exist yet — create one (Type: Add-on) first.
                </p>
              ) : (
                <div className="space-y-2">
                  {linkedAddonRows.map((row) => (
                    <div key={row.addonId} className="flex items-center gap-4">
                      <div className="flex-1">
                        <Checkbox
                          id={`linked-addon-${row.addonId}`}
                          label={row.addonName}
                          checked={row.linked}
                          onChange={() => toggleAddonLinked(row.addonId)}
                        />
                      </div>
                      <div className="w-32">
                        <Input
                          type="number"
                          step="0.01"
                          disabled={!row.linked}
                          value={row.priceCents / 100}
                          onChange={(e) => setAddonPrice(row.addonId, parseFloat(e.target.value) || 0)}
                          placeholder="Price/mo"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FormGroup>
          )}

          {/* Valid From and Valid To - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <FormGroup
              error={touched.validFrom && errors.validFrom ? errors.validFrom : undefined}
              htmlFor="validFrom"
              label="Valid From (Optional)">
              <Input
                name="validFrom"
                type="date"
                value={values.validFrom}
                onChange={handleChange}
                error={!!(touched.validFrom && errors.validFrom)}
              />
            </FormGroup>

            <FormGroup
              error={touched.validTo && errors.validTo ? errors.validTo : undefined}
              htmlFor="validTo"
              label="Valid To (Optional)">
              <Input
                name="validTo"
                type="date"
                value={values.validTo}
                onChange={handleChange}
                error={!!(touched.validTo && errors.validTo)}
              />
            </FormGroup>
          </div>

          {/* Delete Button (only in edit mode) */}
          {editing && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="tertiary"
                onClick={() => setIsDeleteModalOpen(true)}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                Delete Plan
              </Button>
            </div>
          )}
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        variant="danger"
        title="Delete Subscription Plan"
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        isLoading={deleting}
        closeOnConfirm={false}
        description={`Are you sure you want to delete "${initialData?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default CreateEditPlanModal;
